import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import api, { API_BASE_URL } from "../config/api";

const resolveSignatureUrl = (value) => {
  const url = String(value || "").trim();
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;
  return `${API_BASE_URL.replace(/\/$/, "")}/${url.replace(/^\/+/, "")}`;
};

const Profile = () => {
  const user = useSelector((state) => state.auth.user);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState(""); // "success" or "error"
  const [preview, setPreview] = useState("");

  useEffect(() => {
    const currentSignature = resolveSignatureUrl(user?.signature_url);
    if (currentSignature) {
      setPreview(currentSignature);
      return;
    }

    let cancelled = false;
    const loadSignature = async () => {
      try {
        const response = await api.get("/api/auth/check-auth", {
          headers: {
            "Cache-Control": "no-store, no-cache, must-revalidate, proxy-revalidate",
            Pragma: "no-cache",
            Expires: "0",
          },
        });
        const signatureUrl = resolveSignatureUrl(response.data?.user?.signature_url);
        if (!cancelled) {
          setPreview(signatureUrl);
        }
      } catch (err) {
        console.error("load profile signature", err);
      }
    };

    loadSignature();
    return () => {
      cancelled = true;
    };
  }, [user?.signature_url]);

  if (!user) return <div className="p-6">No user loaded.</div>;

  const handleFile = async (file) => {
    if (!file) return;
    setUploading(true);
    setMessage("");
    setMessageType("");
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await api.post(
        "/api/auth/upload-signature",
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      if (response.data.success) {
        setPreview(resolveSignatureUrl(response.data.signature_url));
        setMessage("Signature uploaded successfully!");
        setMessageType("success");
      } else {
        throw new Error(response.data.message || "Upload failed");
      }
    } catch (err) {
      console.error(err);
      setMessage(err.response?.data?.message || err.message || "Upload failed");
      setMessageType("error");
    } finally {
      setUploading(false);
    }
  };

  return (
    <section
      id="profile"
      className="flex flex-col justify-center p-0 font-poppins min-h-screen bg-gray-100 relative overflow-auto"
    >
      <div className="relative flex flex-col justify-center py-6 px-4">
        {/* Enhanced Header */}
        <div className="text-center mb-6">
          <div className="flex items-center justify-center gap-3 mb-3">
            <h1 className="text-4xl font-bold text-blue">My Profile</h1>
          </div>
          <p className="text-grey font-medium">
            Manage your account information and e-signature
          </p>
        </div>

        {/* Modern Form Container */}
        <div className="w-full max-w-7xl mx-auto">
          <div className="rounded-3xl shadow-xl border border-white/20 overflow-hidden">
            {/* Form Header */}
            <div className="bg-blue px-8 py-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-white/20 rounded-2xl">
                  <i className="fas fa-user-circle text-white text-xl"></i>
                </div>
                <div>
                  <h2 className="text-white font-bold text-xl">Account Information</h2>
                  <p className="text-white text-sm">View and manage your profile details</p>
                </div>
              </div>
            </div>

            {/* Form Body */}
            <div className="p-8">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Personal Information Section */}
                <div className="space-y-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-8 h-8 bg-blue/10 rounded-xl flex items-center justify-center">
                      <i className="fas fa-id-card text-blue text-sm"></i>
                    </div>
                    <h3 className="text-blue font-bold text-lg">Personal Info</h3>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="flex items-center gap-2 text-grey font-semibold mb-3 text-sm">
                        <i className="fas fa-user text-blue text-xs"></i>
                        First Name
                      </label>
                      <input
                        type="text"
                        value={user.first_name || ""}
                        disabled
                        className="w-full p-4 border-2 border-gray-200 rounded-2xl bg-gray-50 text-grey cursor-not-allowed"
                      />
                    </div>

                    <div>
                      <label className="flex items-center gap-2 text-grey font-semibold mb-3 text-sm">
                        <i className="fas fa-user text-blue text-xs"></i>
                        Last Name
                      </label>
                      <input
                        type="text"
                        value={user.last_name || ""}
                        disabled
                        className="w-full p-4 border-2 border-gray-200 rounded-2xl bg-gray-50 text-grey cursor-not-allowed"
                      />
                    </div>
                  </div>
                </div>

                {/* Organization Section */}
                <div className="space-y-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-8 h-8 bg-blue/10 rounded-xl flex items-center justify-center">
                      <i className="fas fa-briefcase text-blue text-sm"></i>
                    </div>
                    <h3 className="text-blue font-bold text-lg">Organization</h3>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="flex items-center gap-2 text-grey font-semibold mb-3 text-sm">
                        <i className="fas fa-building text-blue text-xs"></i>
                        Department
                      </label>
                      <input
                        type="text"
                        value={user.department || ""}
                        disabled
                        className="w-full p-4 border-2 border-gray-200 rounded-2xl bg-gray-50 text-grey cursor-not-allowed"
                      />
                    </div>

                    <div>
                      <label className="flex items-center gap-2 text-grey font-semibold mb-3 text-sm">
                        <i className="fas fa-shield-alt text-blue text-xs"></i>
                        Role
                      </label>
                      <input
                        type="text"
                        value={user.role || ""}
                        disabled
                        className="w-full p-4 border-2 border-gray-200 rounded-2xl bg-gray-50 text-grey cursor-not-allowed"
                      />
                    </div>
                  </div>
                </div>

                {/* Account Credentials Section */}
                <div className="space-y-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-8 h-8 bg-blue/10 rounded-xl flex items-center justify-center">
                      <i className="fas fa-key text-blue text-sm"></i>
                    </div>
                    <h3 className="text-blue font-bold text-lg">Credentials</h3>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="flex items-center gap-2 text-grey font-semibold mb-3 text-sm">
                        <i className="fas fa-at text-blue text-xs"></i>
                        Username
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          value={user.user_name || ""}
                          disabled
                          className="w-full p-4 border-2 border-gray-200 rounded-2xl bg-gray-50 text-grey cursor-not-allowed"
                        />
                        <div className="absolute inset-y-0 right-0 flex items-center pr-4">
                          <i className="fas fa-lock text-grey/50 text-sm"></i>
                        </div>
                      </div>
                    </div>

                    <div>
                      <label className="flex items-center gap-2 text-grey font-semibold mb-3 text-sm">
                        <i className="fas fa-envelope text-blue text-xs"></i>
                        Email
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          value={user.email || "N/A"}
                          disabled
                          className="w-full p-4 border-2 border-gray-200 rounded-2xl bg-gray-50 text-grey cursor-not-allowed"
                        />
                        <div className="absolute inset-y-0 right-0 flex items-center pr-4">
                          <i className="fas fa-lock text-grey/50 text-sm"></i>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* E-Signature Section - Full Width */}
                <div className="lg:col-span-3 mt-6 pt-6 border-t border-gray-100">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-8 h-8 bg-blue/10 rounded-xl flex items-center justify-center">
                      <i className="fas fa-signature text-blue text-sm"></i>
                    </div>
                    <h3 className="text-blue font-bold text-lg">E-Signature</h3>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* Current Signature Preview */}
                    <div className="space-y-4">
                      <label className="flex items-center gap-2 text-grey font-semibold text-sm">
                        <i className="fas fa-image text-blue text-xs"></i>
                        Current Signature
                      </label>
                      <div className="border-2 border-dashed border-gray-200 rounded-2xl p-6 bg-gray-50 min-h-[120px] flex items-center justify-center">
                        {preview ? (
                          <img
                            src={preview}
                            alt="signature"
                            className="max-h-24 object-contain"
                          />
                        ) : (
                          <div className="text-center text-grey/50">
                            <i className="fas fa-signature text-3xl mb-2"></i>
                            <p className="text-sm">No signature uploaded</p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Upload Section */}
                    <div className="space-y-4">
                      <label className="flex items-center gap-2 text-grey font-semibold text-sm">
                        <i className="fas fa-upload text-blue text-xs"></i>
                        Upload New Signature
                      </label>
                      <div className="border-2 border-dashed border-gray-200 rounded-2xl p-6 bg-white hover:border-blue hover:bg-blue/5 transition-all duration-300 min-h-[120px] flex items-center justify-center">
                        <label className="cursor-pointer text-center w-full">
                          <div className="flex flex-col items-center gap-3">
                            <div className="w-12 h-12 bg-blue/10 rounded-2xl flex items-center justify-center">
                              <i className={`fas ${uploading ? 'fa-spinner fa-spin' : 'fa-cloud-upload-alt'} text-blue text-xl`}></i>
                            </div>
                            <div>
                              <p className="text-grey font-semibold text-sm">
                                {uploading ? "Uploading..." : "Click to upload"}
                              </p>
                              <p className="text-grey/50 text-xs mt-1">
                                PNG, JPG up to 5MB
                              </p>
                            </div>
                          </div>
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            disabled={uploading}
                            onChange={(e) => handleFile(e.target.files?.[0])}
                          />
                        </label>
                      </div>

                      {/* Message */}
                      {message && (
                        <div className={`flex items-center gap-2 p-3 rounded-xl text-sm ${
                          messageType === "success"
                            ? "bg-green-50 text-green-600 border border-green-200"
                            : "bg-red-50 text-red-600 border border-red-200"
                        }`}>
                          <i className={`fas ${messageType === "success" ? "fa-check-circle" : "fa-exclamation-circle"}`}></i>
                          {message}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Profile;
